import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativePageRoot } from '../styles/iosNativePageCss';
import { Loader } from 'lucide-react';
import { useAWSAuth } from '../contexts/AWSAuthContext';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;

  ${iosNativePageRoot}
`;

const LoadingCard = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  max-width: 400px;
  animation: slideUp 0.5s ease-out;
`;

const LoadingSpinner = styled(Loader)`
  width: 48px;
  height: 48px;
  color: ${theme.colors.primary[500]};
  margin: 0 auto 1.5rem;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.status.error};
  margin: 1rem 0 0;
`;

/**
 * OAuth Callback Handler for Social Sign-In
 * Handles redirect from Cognito after social authentication
 */
const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOAuthCallback } = useAWSAuth();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const processedRef = useRef(false);

  useEffect(() => {
    // Guard against re-firing: authorization codes are single-use,
    // so we must only attempt the token exchange once.
    if (processedRef.current) return;
    processedRef.current = true;

    const handleCallback = async () => {
      try {
        setLoading(true);
        
        // Get URL parameters
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const errorParam = params.get('error');
        const errorDescription = params.get('error_description');
        const state = params.get('state');

        if (errorParam) {
          setError(errorDescription || 'Authentication failed');
          setLoading(false);
          setTimeout(() => navigate('/signin'), 3000);
          return;
        }

        if (!code) {
          setError('No authorization code received. Please try signing in again.');
          setLoading(false);
          setTimeout(() => navigate('/signin'), 3000);
          return;
        }

        // Handle OAuth callback using auth context
        await handleOAuthCallback(code, state || undefined);
        
        // Get referral source if stored
        const referralSource = sessionStorage.getItem('referralSource');
        if (referralSource) {
          sessionStorage.removeItem('referralSource');
        }

        // Redirect to home or the page they were trying to access
        const from = sessionStorage.getItem('redirectAfterAuth') || '/';
        sessionStorage.removeItem('redirectAfterAuth');
        navigate(from, { replace: true });
      } catch (error: any) {
        console.error('OAuth callback error:', error);
        setError(error.message || 'Authentication failed. Please try again.');
        setLoading(false);
        setTimeout(() => navigate('/signin'), 3000);
      }
    };

    handleCallback();
  }, []);

  return (
    <PageContainer>
      <LoadingCard>
        {error ? (
          <>
            <ErrorText>{error}</ErrorText>
            <LoadingText>Redirecting to sign in...</LoadingText>
          </>
        ) : (
          <>
            <LoadingSpinner />
            <LoadingText>{loading ? 'Completing sign-in...' : 'Sign-in successful!'}</LoadingText>
          </>
        )}
      </LoadingCard>
    </PageContainer>
  );
};

export default OAuthCallbackPage;


